/**
 * Connect a git server's OAuth login — the shared popup flow.
 *
 * One implementation shared by `GitServerAuthRow` (Settings) and
 * `ProjectSelector` (the empty-state connect prompt), copied from
 * `LoginsPanel`'s direct-OIDC flow: start the login, stash the PKCE
 * state/verifier for the popup's callback page, open the popup, then wait
 * for its `postMessage` on success.
 *
 * Invalidates both `['logins']` (the Logins panel) and `['gitCredentials']`
 * (this feature's `useGitCredentials` query key) so every consumer of
 * either query reflects the new connection without a manual refresh.
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStartLogin } from '@features/logins/hooks/useLogins';
import { saveLoginFlow, LOGIN_COMPLETE_MESSAGE } from '@features/logins/loginFlowStorage';
import { ROUTES } from '@utils/constants';

export function useConnectGitServer(): {
  connect: (providerName: string) => Promise<void>;
  isConnecting: boolean;
  error: string | null;
} {
  const startLogin = useStartLogin();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: unknown } | null;
      if (data?.type !== LOGIN_COMPLETE_MESSAGE) return;
      void queryClient.invalidateQueries({ queryKey: ['logins'] });
      void queryClient.invalidateQueries({ queryKey: ['gitCredentials'] });
    };
    window.addEventListener('message', handleMessage);
    return () => { window.removeEventListener('message', handleMessage); };
  }, [queryClient]);

  const connect = async (providerName: string): Promise<void> => {
    setError(null);
    try {
      const redirectUri = `${window.location.origin}${ROUTES.LOGIN_CALLBACK}`;
      const resp = await startLogin.mutateAsync({ name: providerName, redirectUri });
      saveLoginFlow({
        state: resp.state,
        code_verifier: resp.code_verifier,
        provider: providerName,
        redirect_uri: redirectUri,
      });
      window.open(resp.url, 'sanctum-login', 'width=600,height=700');
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to start login for ${providerName}`);
    }
  };

  return { connect, isConnecting: startLogin.isPending, error };
}
